import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { t } from '../theme';
import { useStore } from '../store';

/**
 * One household account, created on first visit.
 *
 * Real <input> fields with the right autocomplete hints, so iCloud Keychain
 * and 1Password can generate and fill the password — which matters far more
 * than any policy we could enforce, given this is typed on a tablet.
 */
export function Gate() {
  const needsSetup = useStore((s) => s.needsSetup);
  const login = useStore((s) => s.login);
  const register = useStore((s) => s.register);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [tokenRequired, setTokenRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const firstField = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    void api
      .authState()
      .then((state) => setTokenRequired(state.setupTokenRequired))
      .catch(() => {});
    firstField.current?.focus();
  }, []);

  const submit = async () => {
    if (busy) return;
    setError(null);

    if (needsSetup && password !== confirm) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    setBusy(true);
    const message = needsSetup
      ? await register(username.trim(), password, tokenRequired ? setupToken : undefined)
      : await login(username.trim(), password);
    setBusy(false);

    if (message) {
      setError(message);
      setPassword('');
      setConfirm('');
    }
  };

  const field = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    opts: { type?: string; autoComplete: string; ref?: typeof firstField } = { autoComplete: 'off' },
  ) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
      <span style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: t.inkFaint }}>
        {label}
      </span>
      <input
        ref={opts.ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && void submit()}
        type={opts.type ?? 'text'}
        autoComplete={opts.autoComplete}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="go"
        style={{
          width: '100%',
          background: 'oklch(0.2 0.012 60)',
          border: '1px solid oklch(1 0 0 / 0.1)',
          borderRadius: 18,
          padding: '18px 20px',
          color: t.ink,
          caretColor: t.accent,
          fontSize: 24,
          fontWeight: 500,
          outline: 'none',
        }}
      />
    </label>
  );

  return (
    <div
      style={{
        width: '100vw',
        height: '100dvh',
        background: t.bgGradient,
        color: t.ink,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
        padding: 40,
      }}
    >
      <div style={{ fontSize: 52, fontWeight: 500, letterSpacing: '-0.045em' }}>
        frisk<span style={{ color: t.accent }}>.</span>
      </div>
      <div style={{ fontSize: 21, color: t.inkMuted, textAlign: 'center', maxWidth: 520, textWrap: 'pretty' }}>
        {needsSetup
          ? 'Noch kein Konto. Lege jetzt das Haushalts-Konto an — danach ist das Panel gesperrt.'
          : 'Anmelden'}
      </div>

      {/* A real form element is what lets a password manager offer to save. */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        style={{ width: 460, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}
      >
        {field('BENUTZERNAME', username, setUsername, { autoComplete: 'username', ref: firstField })}
        {field('PASSWORT', password, setPassword, {
          type: 'password',
          autoComplete: needsSetup ? 'new-password' : 'current-password',
        })}
        {needsSetup &&
          field('PASSWORT WIEDERHOLEN', confirm, setConfirm, { type: 'password', autoComplete: 'new-password' })}
        {needsSetup && tokenRequired &&
          field('EINRICHTUNGS-CODE', setupToken, setSetupToken, { autoComplete: 'off' })}

        <button
          type="submit"
          disabled={busy || !username.trim() || !password}
          style={{
            height: 78,
            marginTop: 6,
            borderRadius: 20,
            border: 'none',
            background: t.accent,
            color: t.accentInk,
            fontSize: 24,
            fontWeight: 600,
            fontFamily: t.sans,
            cursor: busy ? 'wait' : 'pointer',
            opacity: busy || !username.trim() || !password ? 0.4 : 1,
          }}
        >
          {needsSetup ? 'Konto anlegen' : 'Anmelden'}
        </button>
      </form>

      {error && <div style={{ fontSize: 19, color: 'oklch(0.78 0.14 30)' }}>{error}</div>}

      {needsSetup && (
        <div style={{ fontSize: 16, color: t.inkFaint, maxWidth: 520, textAlign: 'center', textWrap: 'pretty' }}>
          Mindestens 10 Zeichen. Am besten vom Passwort-Manager erzeugen lassen.
        </div>
      )}
    </div>
  );
}
